import { Box } from '@mui/material';
import {
  useDeleteChallengeById,
  useDeleteExerciseById,
  useDeleteResourceById,
} from '_api/mutations';
import { Challenge, Exercise, Resource, LibraryItem } from '_api/types';
import {
  ChallengeRouteMap,
  ExerciseRouteMap,
  LIBRARYITEMTYPE,
  ResourceRouteMap,
  TherapyTypeValue,
} from '_constants';
import { SearchInput, ThumbnailView } from 'components/atoms';
import { ActionsFlexBox } from 'components/molecules';
import { ViewButtons } from 'components/molecules/types';
import {
  AddChallengeToPatientModal,
  AddExerciseToPatientModal,
  AddResourceToPatientModal,
} from 'components/organisms/modals';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigateTo } from 'utils';
import { useSearchResults } from 'utils/hooks';
import { useMyLibrary } from 'utils/hooks/ctx-hooks';

export const LibraryManagement = () => {
  const { existingSearchTerm, updateSearchTerm } = useSearchResults();
  const navigate = useNavigateTo();
  const { myLibraryData, isLoading } = useMyLibrary();
  const { mutateAsync: deleteExercise } = useDeleteExerciseById();
  const { mutateAsync: deleteChallenge } = useDeleteChallengeById();
  const { mutateAsync: deleteResource } = useDeleteResourceById();

  const [id, setId] = useState(0);
  const [content, setContent] = useState<Challenge | Exercise | Resource | undefined>(
    undefined,
  );
  const [openExercise, setOpenExercise] = useState(false);
  const [openChallenge, setOpenChallenge] = useState(false);
  const [openResource, setOpenResource] = useState(false);
  const [searchTerm, setSearchTerm] = useState(existingSearchTerm || '');
  const [filteredData, setFilteredData] = useState<LibraryItem[]>([]);

  useEffect(() => {
    if (myLibraryData) {
      setFilteredData(myLibraryData);
    }
  }, [myLibraryData]);

  useEffect(() => setSearchTerm(existingSearchTerm || ''), [existingSearchTerm]);

  useMemo(() => {
    if (myLibraryData) {
      const lowercasedSearchTerm = searchTerm.toLowerCase();
      const filtered = myLibraryData.filter(({ data, type }) => {
        const title = data.title.toLowerCase();
        let therapyType = '';
        let therapyValue = '';
        let categories = '';
        if (type == LIBRARYITEMTYPE.EXERCISE) {
          const ex = data as Exercise;
          therapyType = ex.defaultTherapyType?.toString().toLowerCase() || '';
          therapyValue = ex.defaultTherapyType
            ? TherapyTypeValue[ex.defaultTherapyType].toLowerCase()
            : '';
          categories = ex.category
            .map(
              (cate, i) =>
                `${cate.title.toLowerCase()}${i < ex.category.length - 1 ? ', ' : ''}`,
            )
            .toString();
        }
        return (
          title.includes(lowercasedSearchTerm) ||
          therapyType.includes(lowercasedSearchTerm) ||
          therapyValue.includes(lowercasedSearchTerm) ||
          categories.includes(lowercasedSearchTerm)
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, myLibraryData]);

  const handleAddOnClick = (item: LibraryItem) => {
    setId(item.data.id);
    setContent(item.data);
    item.type === LIBRARYITEMTYPE.EXERCISE
      ? setOpenExercise(true)
      : item.type === LIBRARYITEMTYPE.CHALLENGE
        ? setOpenChallenge(true)
        : setOpenResource(true);
  };

  const handleViewOnClick = (id: number, type: string | undefined) => {
    if (type && type === LIBRARYITEMTYPE.EXERCISE) {
      navigate(ExerciseRouteMap.DETAIL, id.toString());
      return;
    }
    if (type && type === LIBRARYITEMTYPE.CHALLENGE) {
      navigate(ChallengeRouteMap.DETAIL, id.toString());
    }

    navigate(ResourceRouteMap.DETAIL, id.toString());
  };

  const handleEditOnClick = (id: number, type: string | undefined) => {
    if (type && type === LIBRARYITEMTYPE.EXERCISE) {
      navigate(ExerciseRouteMap.EDIT, id.toString());
      return;
    }
    if (type && type === LIBRARYITEMTYPE.CHALLENGE) {
      navigate(ChallengeRouteMap.EDIT, id.toString());
    }

    navigate(ResourceRouteMap.EDIT, id.toString());
  };

  const handleDeleteOnClick = (id: number, type: string | undefined) => {
    if (type && type === LIBRARYITEMTYPE.EXERCISE) {
      deleteExercise({ id });
      return;
    }
    if (type && type === LIBRARYITEMTYPE.CHALLENGE) {
      deleteChallenge({ id });
      return;
    }

    if (type && type === LIBRARYITEMTYPE.RESOURCE) {
      deleteResource({ id });
      return;
    }
  };

  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
    updateSearchTerm(searchTerm);
  };

  const actionButtons: ViewButtons = {
    viewButton: true,
    editButton: true,
    deleteButton: true,
    addButton: true,
  };

  const tableColumns = useMemo<MRT_ColumnDef<LibraryItem>[]>(
    () => [
      {
        header: 'Exercise',
        enableSorting: false,
        Cell: ({ row }) => (
          <ThumbnailView fullImageUrl={row.original.data.fullImageUrl} rowView={true} />
        ),
      },
      {
        header: 'Title',
        accessorFn: (row) => row.data.title,
      },
      {
        header: 'Type',
        accessorFn: (row) => {
          if (row.type === LIBRARYITEMTYPE.EXERCISE) {
            const data = row.data as Exercise;
            return data.defaultTherapyType
              ? TherapyTypeValue[data.defaultTherapyType]
              : '';
          }
          return '';
        },
      },
      {
        header: 'Category',
        accessorFn: (row) => {
          if (row.type === LIBRARYITEMTYPE.EXERCISE) {
            const data = row.data as Exercise;
            return data.category.map(
              (cate, i) => `${cate.title}${i < data.category.length - 1 ? ', ' : ''}`,
            );
          }
          return '';
        },
      },
      {
        header: 'Actions',
        enableSorting: false,
        accessorFn: (row) => (
          <ActionsFlexBox
            msg={row.type.toLowerCase()}
            addOnClick={() => handleAddOnClick(row)}
            viewOnClick={() => handleViewOnClick(row.data.id, row.type)}
            editOnClick={() => handleEditOnClick(row.data.id, row.type)}
            deleteOnClick={() => handleDeleteOnClick(row.data.id, row.type)}
            actionButtons={actionButtons}
          />
        ),
        size: 10,
      },
    ],
    [],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 5, marginLeft: 'auto' }}>
          <SearchInput value={searchTerm} onSearch={handleSearch} />
        </Box>
      </Box>
      <Box sx={{ marginTop: 10 }}>
        {!isLoading && filteredData ? (
          <MaterialReactTable
            columns={tableColumns}
            data={filteredData}
            enableSorting={true}
            enableColumnActions={false}
            enableTopToolbar={false}
          />
        ) : null}
      </Box>
      <AddChallengeToPatientModal
        content={content as Challenge}
        open={openChallenge && content != undefined}
        onClose={() => {
          setOpenChallenge(false);
          setContent(undefined);
        }}
        id={id}
      />
      <AddExerciseToPatientModal
        content={content as Exercise}
        open={openExercise && content != undefined}
        onClose={() => {
          setOpenExercise(false);
          setContent(undefined);
        }}
        id={id}
      />
      <AddResourceToPatientModal
        open={openResource && content != undefined}
        onClose={() => {
          setOpenResource(false);
          setContent(undefined);
        }}
        content={content as Resource}
        id={id}
      />
    </Box>
  );
};
